import React, { useState, useEffect } from "react";
import { Header, Icon, Button, Form, Grid, Card, Statistic, Divider, Loader, Segment } from "semantic-ui-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { CashRegister } from "../../../api";
import { useAuth } from "../../../hooks";
import moment from "moment";
import "./FinancialReports.scss";

const cashRegisterController = new CashRegister();

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444"];

export function FinancialReports() {
  const { accessToken } = useAuth();
  
  // Date Filters
  const [startDate, setStartDate] = useState(moment().subtract(30, "days").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [pieData, setPieData] = useState([]);

  const handleFetchReport = async () => {
    try {
      setLoading(true);
      const res = await cashRegisterController.getGeneralIncomeReport(accessToken, startDate, endDate);
      if (res && res.success) {
        setReportData(res.summary);
        
        // Formatear datos para el gráfico de torta (PieChart)
        const categories = res.summary.byCategory || {};
        const formattedPie = [
          { name: "Mensualidades", value: parseFloat(categories.TUITION || 0) + parseFloat(categories.MONTHLY_PENSION || 0) },
          { name: "Mora / Intereses", value: parseFloat(categories.LATE_FEE || 0) },
          { name: "Otros Conceptos", value: parseFloat(categories.OTHER || 0) }
        ].filter(item => item.value > 0);
        setPieData(formattedPie);

        // Agrupar ingresos confirmados por fecha para el gráfico de línea/área
        const dailyGroup = {};
        res.incomes?.forEach(inc => {
          const day = moment(inc.recordedDate || inc.createdAt).format("DD/MM");
          dailyGroup[day] = (dailyGroup[day] || 0) + parseFloat(inc.totalAmount || 0);
        });

        const formattedArea = Object.keys(dailyGroup).map(key => ({
          date: key,
          monto: dailyGroup[key]
        })).sort((a, b) => moment(a.date, "DD/MM") - moment(b.date, "DD/MM"));
        
        setChartData(formattedArea);
      }
    } catch (error) {
      console.error("Error al generar reporte financiero", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleFetchReport();
  }, [accessToken]);

  return (
    <div className="financial-reports">
      <div className="financial-reports__header">
        <Header as="h2">
          <Icon name="chart line" color="blue" />
          <Header.Content>
            Reportes y Estadísticas Financieras
            <Header.Subheader>Visualización de ingresos, distribución de categorías y análisis de cobros</Header.Subheader>
          </Header.Content>
        </Header>

        {/* Date Filter Bar */}
        <Form className="financial-reports__filters" onSubmit={(e) => { e.preventDefault(); handleFetchReport(); }}>
          <Form.Group widths="equal" style={{ margin: 0, alignItems: "flex-end" }}>
            <Form.Input
              type="date"
              label="Fecha Inicio"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <Form.Input
              type="date"
              label="Fecha Fin"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
            <Form.Button primary type="submit" icon="refresh" content="Filtrar" />
          </Form.Group>
        </Form>
      </div>

      {loading ? (
        <Loader active inline="centered" content="Generando reportes financieros..." />
      ) : !reportData ? (
        <Segment placeholder textAlign="center">
          <Header icon>
            <Icon name="warning sign" color="yellow" />
            No se pudo obtener información del período seleccionado.
          </Header>
        </Segment>
      ) : (
        <>
          {/* Top Stat Cards */}
          <Card.Group itemsPerRow={3} stackable className="financial-reports__stats">
            <Card className="stat-card blue">
              <Card.Content>
                <Statistic size="small">
                  <Statistic.Value>S/ {Number(reportData.totalIncome || 0).toFixed(2)}</Statistic.Value>
                  <Statistic.Label>Ingresos Totales</Statistic.Label>
                </Statistic>
              </Card.Content>
            </Card>
            <Card className="stat-card green">
              <Card.Content>
                <Statistic size="small">
                  <Statistic.Value>S/ {Number(reportData.byCategory?.LATE_FEE || 0).toFixed(2)}</Statistic.Value>
                  <Statistic.Label>Recaudado por Mora</Statistic.Label>
                </Statistic>
              </Card.Content>
            </Card>
            <Card className="stat-card purple">
              <Card.Content>
                <Statistic size="small">
                  <Statistic.Value>{reportData.recordCount || 0}</Statistic.Value>
                  <Statistic.Label>Transacciones Validadas</Statistic.Label>
                </Statistic>
              </Card.Content>
            </Card>
          </Card.Group>

          {/* Charts Grid */}
          <Grid columns={2} stackable style={{ marginTop: "1rem" }}>
            <Grid.Row>
              {/* Daily Revenue Area Chart */}
              <Grid.Column width={10}>
                <Segment className="chart-segment">
                  <Header as="h3">Flujo de Ingresos Diarios (S/)</Header>
                  <Divider />
                  <div style={{ width: "100%", height: 300 }}>
                    {chartData.length === 0 ? (
                      <div className="no-data-msg">No hay transacciones registradas en este período.</div>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id="colorMonto" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-grey-light)" />
                          <XAxis dataKey="date" stroke="var(--text-color-70)" />
                          <YAxis stroke="var(--text-color-70)" />
                          <Tooltip contentStyle={{ background: "var(--card-bg)", border: "1px solid var(--border-grey-light)", color: "var(--text-color)" }} />
                          <Area type="monotone" dataKey="monto" name="Recaudado (S/)" stroke="#3b82f6" fillOpacity={1} fill="url(#colorMonto)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </Segment>
              </Grid.Column>

              {/* Category Pie Chart */}
              <Grid.Column width={6}>
                <Segment className="chart-segment">
                  <Header as="h3">Distribución por Categorías</Header>
                  <Divider />
                  <div style={{ width: "100%", height: 300, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    {pieData.length === 0 ? (
                      <div className="no-data-msg">Sin datos de distribución.</div>
                    ) : (
                      <ResponsiveContainer width="100%" height="80%">
                        <PieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => `S/ ${value.toFixed(2)}`} contentStyle={{ background: "var(--card-bg)", border: "1px solid var(--border-grey-light)", color: "var(--text-color)" }} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </Segment>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </>
      )}
    </div>
  );
}
